import { useLiveQuery } from "dexie-react-hooks";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { entityTable, ENTITY_TABLES, getDb, type EntityTable } from "../db/dexie";
import { useSyncStore } from "../store/syncStore";
import { describeRecord, explainRejection, groupFailed, type FailedGroup } from "../sync/rejections";
import { getSyncClient, mergePendingPatches } from "../sync/SyncClient";

const KIND_LABEL = { new: "New", edit: "Edit", delete: "Delete" } as const;

function statusText(status: string, pending: number, lastError: string | null): string {
  if (status === "offline") {
    return pending > 0
      ? `Offline. ${pending} ${pending === 1 ? "change" : "changes"} will send when you're back online.`
      : "Offline. Everything you've saved is on this device.";
  }
  if (status === "syncing") return "Syncing…";
  if (status === "error") return `Can't reach the server right now${lastError ? ` (${lastError})` : ""}. It will keep trying.`;
  return pending > 0 ? `${pending} ${pending === 1 ? "change" : "changes"} waiting to send.` : "All changes saved to the server.";
}

export function SettingsSync() {
  const status = useSyncStore((s) => s.status);
  const pending = useSyncStore((s) => s.pendingCount);
  const lastError = useSyncStore((s) => s.lastError);
  const lastSyncAt = useSyncStore((s) => s.lastSyncAt);

  const failed = useLiveQuery(async () => {
    const db = getDb();
    const groups = groupFailed(await db.deadLetter.toArray());
    return Promise.all(
      groups.map(async (g) => {
        const isTable = (ENTITY_TABLES as readonly string[]).includes(g.entity);
        const local = isTable
          ? ((await entityTable(db, g.entity as EntityTable).get(g.id)) as Record<string, unknown> | undefined)
          : undefined;
        return { group: g, data: { ...(local ?? {}), ...(mergePendingPatches(g.rows, null) ?? {}) } };
      }),
    );
  }, []);

  return (
    <div className="stack">
      <Link to="/settings" className="muted back-link">
        ← Settings
      </Link>
      <h1 className="screen-title">Sync</h1>
      <div className="card stack">
        <div data-testid="sync-status">{statusText(status, pending, lastError)}</div>
        <div className="muted">
          Last sync: {lastSyncAt ? new Date(lastSyncAt).toLocaleString() : "—"}
        </div>
        <Button variant="primary" onClick={() => void getSyncClient().syncNow()}>
          Sync now
        </Button>
      </div>

      {(failed ?? []).length > 0 ? (
        <section className="card stack" aria-label="Changes that didn't sync" data-testid="failed-list">
          <div>
            <strong>Changes that didn't sync</strong>
            <p className="muted" style={{ margin: "0.25rem 0 0" }}>
              The server refused these. Retry sends them again; Discard puts each item back the way the server has it.
            </p>
          </div>
          {(failed ?? []).map(({ group, data }) => (
            <FailedItem key={group.key} group={group} title={describeRecord(group.entity, data)} />
          ))}
        </section>
      ) : null}
    </div>
  );
}

function FailedItem({ group, title }: { group: FailedGroup; title: string }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const why = explainRejection(group.latest.reason);
  const when = new Date(group.latest.at).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="failed-item stack" data-testid="failed-item" aria-label={title}>
      <div>
        <div>
          <strong>{KIND_LABEL[group.kind]}</strong> · {title}
        </div>
        <div className="muted">{when}</div>
      </div>
      <p className="legal-flag" style={{ margin: 0 }}>
        {why.text}
      </p>
      {error ? (
        <p className="legal-flag" role="alert" style={{ margin: 0 }}>
          {error}
        </p>
      ) : null}
      <div className="row">
        {why.canRetry ? (
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => void run(() => getSyncClient().retryFailed(group.entity, group.id))}
          >
            Retry
          </Button>
        ) : null}
        <Button
          variant={confirm ? "danger" : "default"}
          disabled={busy}
          onClick={() => {
            if (!confirm) {
              setConfirm(true);
              return;
            }
            void run(() => getSyncClient().discardFailed(group.entity, group.id));
          }}
        >
          {confirm ? "Tap again to discard" : "Discard"}
        </Button>
      </div>
      <details>
        <summary className="muted">Details</summary>
        <div className="muted" style={{ overflowWrap: "anywhere" }}>
          {group.entity} {group.id} · {group.rows.length} {group.rows.length === 1 ? "change" : "changes"} · server said: {group.latest.reason}
        </div>
      </details>
    </article>
  );
}
